import React, { useState, useEffect } from 'react';
import { TextField, Button, Box } from '@mui/material';
import Modal from 'components/_layouts/Modal';
import { enqueueSnackbar } from 'notistack';
import { Attachment } from 'views/App/Portal/Policy/_types/manuals/draft';
import useManageAttachment from '../../../_data_sources/useManageAttachment';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';
import { translate as t } from 'hooks/useTranslations';

interface AttachmentEditDialogProps {
  open: boolean;
  manualId: string;
  attachment: Attachment;
  onClose: () => void;
  onUpdated: () => void;
}

const AttachmentEditDialog: React.FC<AttachmentEditDialogProps> = ({
  open,
  manualId,
  attachment,
  onClose,
  onUpdated,
}) => {
  const { updateAttachment } = useManageAttachment();
  const { getLock, removeLock } = useManageManual();

  const getBaseName = (fileName: string): string => {
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex > 0 ? fileName.substring(0, dotIndex) : fileName;
  };
  const getExtension = (fileName: string): string => {
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex > 0 ? fileName.substring(dotIndex) : '';
  };

  const [newBaseName, setNewBaseName] = useState(getBaseName(attachment.name));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setNewBaseName(getBaseName(attachment.name));
  }, [attachment]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await new Promise<void>((resolve, reject) => {
        getLock(
          manualId,
          () => resolve(),
          (err) => reject(err)
        );
      });

      const extension = getExtension(attachment.name);
      const updatedName = newBaseName + extension;

      await updateAttachment({
        manualId,
        attachmentId: attachment.id,
        name: updatedName,
        size: attachment.size,
      });

      await new Promise<void>((resolve, reject) => {
        removeLock(
          manualId,
          () => resolve(),
          (err) => reject(err)
        );
      });

      enqueueSnackbar(t('draftManuals.attachmentLibrary.editedAttachment'), { variant: 'success' });
      onUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating attachment', error);
      enqueueSnackbar(t('draftManuals.attachmentLibrary.errorEditedAttachment'), { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const actions = (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, width: '100%' }}>
      <Button
        sx={{ width: '100%' }}
        color='neutral'
        onClick={onClose}
        disabled={isSaving}
      >
        Cancel
      </Button>
      <Button
        sx={{ width: '100%' }}
        onClick={handleSave}
        variant='contained'
        disabled={isSaving}
      >
        Save
      </Button>
    </Box>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('draftManuals.attachmentLibrary.columns.edit')}
      actions={actions}
      size='xs'
      onSubmit={handleSave}
    >
      <TextField
        fullWidth
        label='File Name'
        value={newBaseName}
        onChange={(e) => setNewBaseName(e.target.value)}
      />
    </Modal>
  );
};

export default AttachmentEditDialog;
